import {
  IonButton,
  IonCheckbox,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
} from "@ionic/react";
import React from "react";
import { bookOutline } from "ionicons/icons";
import clsx from "clsx";

import { Context, UPDATE_SUBJECT_SUCCESS_MESSAGE } from "../../store/teacher";
import { ISubject, ISubjectCategory } from "../../state";
import triangleIcon from "../../assets/icons/triangle_down.svg";
import trashIcon from "../../assets/icons/trash_outline.svg";
import pencilIcon from "../../assets/icons/pencil.svg";
import closeCircleIcon from "../../assets/icons/close_circle_dark.svg";
import checkCircleIcon from "../../assets/icons/check_circle_dark.svg";
import ModalConfirm from "../ModalConfirm";
import ToolTip from "../../atoms/ToolTip";

import styles from "./SubjectSelector.module.scss";

export interface SubjectSelectorProps {
  isOpen: boolean;
  subjects: ISubject[];
  subjectCategories: ISubjectCategory[];
  selected_subject_ids: number[];
  single?: boolean;
  calling: boolean;
  className?: string;
  onClick: () => void;
  update: (args: { [key: string]: any }) => void;
  onCreateOriginalSubject: (name: string) => void;
  onUpdateOriginalSubject: (id: number, name: string) => void;
  onDeleteOriginalSubject: (id: number) => void;
}

const SubjectSelector = (props: SubjectSelectorProps) => {
  const { contextState } = React.useContext(Context);
  const [originalSubjectName, setOriginalSubjectName] = React.useState("");
  const [pendingSubjectName, setPendingSubjectName] = React.useState<
    string | null
  >(null);
  const [editingSubjectId, setEditingSubjectId] = React.useState<number | null>(
    null,
  );
  const [editingName, setEditingName] = React.useState("");
  const [deleteTargetId, setDeleteTargetId] = React.useState<number | null>(
    null,
  );

  React.useEffect(() => {
    if (!props.isOpen) {
      setEditingSubjectId(null);
      setEditingName("");
      setDeleteTargetId(null);
    }
  }, [props.isOpen]);

  React.useEffect(() => {
    if (contextState.success_message === UPDATE_SUBJECT_SUCCESS_MESSAGE) {
      setEditingSubjectId(null);
      setEditingName("");
    }
  }, [contextState.success_message]);

  const prevLastCreatedSubject = React.useRef(
    contextState.last_created_subject,
  );
  React.useEffect(() => {
    if (contextState.last_created_subject === prevLastCreatedSubject.current) {
      return;
    }
    prevLastCreatedSubject.current = contextState.last_created_subject;
    if (
      pendingSubjectName &&
      contextState.last_created_subject?.name === pendingSubjectName
    ) {
      setOriginalSubjectName("");
      setPendingSubjectName(null);
    }
  }, [contextState.last_created_subject, pendingSubjectName]);

  const masterSubjects = props.subjects.filter(
    subject => subject.school_id === null,
  );
  const originalSubjects = props.subjects.filter(
    subject => subject.school_id !== null,
  );
  const categorySections = props.subjectCategories
    .map(category => ({
      category,
      subjects: masterSubjects.filter(
        subject => subject.category_id === category.id,
      ),
    }))
    .filter(section => section.subjects.length > 0);

  const displayNames =
    props.selected_subject_ids.length === 0
      ? "未選択"
      : props.selected_subject_ids.length >= 2
      ? `${
          props.subjects.find(
            subject => subject.id === props.selected_subject_ids[0],
          )?.name
        } ...他${props.selected_subject_ids.length - 1}`
      : props.subjects.find(
          subject => subject.id === props.selected_subject_ids[0],
        )?.name;

  const toggleSubject = (subjectId: number) => {
    // 編集時は1BOX=1教科のため単一選択(選択中の教科をタップしても解除しない)
    if (props.single) {
      props.update({ selected_subject_ids: [subjectId] });
      return;
    }
    if (props.selected_subject_ids.includes(subjectId)) {
      props.update({
        selected_subject_ids: props.selected_subject_ids.filter(
          id => id !== subjectId,
        ),
      });
    } else {
      props.update({
        selected_subject_ids: [...props.selected_subject_ids, subjectId],
      });
    }
  };

  const submitOriginalSubject = () => {
    const name = originalSubjectName.trim();
    if (!name || props.calling) {
      return;
    }
    props.onCreateOriginalSubject(name);
    setPendingSubjectName(name);
  };

  const renderSubjectCheckbox = (subject: ISubject) => (
    <li
      key={subject.id}
      className={styles.subjectCheckbox}
      onClick={() => toggleSubject(subject.id)}
    >
      <IonCheckbox
        checked={props.selected_subject_ids.includes(subject.id)}
        className={styles.checkbox}
      />
      <span className={styles.subjectName}>{subject.name}</span>
    </li>
  );

  const startEditSubject = (subject: ISubject) => {
    setEditingSubjectId(subject.id);
    setEditingName(subject.name);
  };

  const submitEditSubject = () => {
    if (editingSubjectId === null || props.calling) {
      return;
    }
    const name = editingName.trim();
    if (!name) {
      return;
    }
    props.onUpdateOriginalSubject(editingSubjectId, name);
  };

  const renderOriginalSubject = (subject: ISubject) => {
    if (editingSubjectId === subject.id) {
      return (
        <li key={subject.id} className={styles.originalEditor}>
          <IonInput
            className={styles.editorInput}
            value={editingName}
            onIonChange={e => setEditingName(e.detail.value ?? "")}
          />
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => {
              setEditingSubjectId(null);
              setEditingName("");
            }}
          >
            <IonIcon icon={closeCircleIcon} className={styles.circleIcon} />
          </button>
          <button
            type="button"
            className={styles.iconButton}
            disabled={props.calling || !editingName.trim()}
            onClick={submitEditSubject}
          >
            <IonIcon icon={checkCircleIcon} className={styles.circleIcon} />
          </button>
        </li>
      );
    }
    return (
      <li
        key={subject.id}
        className={styles.subjectCheckbox}
        onClick={event => {
          if ((event.target as Element).closest(`.${styles.subjectActions}`)) {
            return;
          }
          toggleSubject(subject.id);
        }}
      >
        <IonCheckbox
          checked={props.selected_subject_ids.includes(subject.id)}
          className={styles.checkbox}
        />
        <span className={styles.subjectName}>{subject.name}</span>
        <div className={styles.subjectActions}>
          <ToolTip
            content="この教科に紐づくデータがあるため削除できません"
            placement="bottomLeft"
            multiline
            isEnabled={!subject.is_deletable}
          >
            <button
              type="button"
              className={styles.iconButton}
              disabled={props.calling || !subject.is_deletable}
              onClick={() => setDeleteTargetId(subject.id)}
            >
              <IonIcon icon={trashIcon} className={styles.trashIcon} />
            </button>
          </ToolTip>
          <button
            type="button"
            className={styles.iconButton}
            onClick={() => startEditSubject(subject)}
          >
            <IonIcon icon={pencilIcon} className={styles.pencilIcon} />
          </button>
        </div>
      </li>
    );
  };

  return (
    <>
      <IonItem
        className={clsx(styles.subjectItemToggle, props.className || "")}
        onClick={props.onClick}
      >
        <IonIcon icon={bookOutline} slot="start" className={styles.bookIcon} />
        <IonLabel className={styles.title}>教科を選択</IonLabel>
        <div className={styles.selectedNames}>{displayNames}</div>
        <IonIcon
          icon={triangleIcon}
          slot="end"
          className={clsx(
            styles.triangleIcon,
            props.isOpen && styles.triangleIconOpen,
          )}
        />
      </IonItem>
      <div className={props.isOpen ? styles.subjectArea : styles.close}>
        {categorySections.map(section => (
          <div key={section.category.id} className={styles.categoryRow}>
            <div className={styles.categoryName}>{section.category.name}</div>
            <ul className={styles.subjectList}>
              {section.subjects.map(renderSubjectCheckbox)}
            </ul>
          </div>
        ))}
        <div className={styles.originalSection}>
          <div className={styles.categoryName}>オリジナル</div>
          {originalSubjects.length > 0 && (
            <ul className={styles.subjectList}>
              {originalSubjects.map(renderOriginalSubject)}
            </ul>
          )}
          <p className={styles.originalNote}>
            上記にない教科は、ここで自由に登録できます。例:野球部、美化委員会、特進英語など
          </p>
          <div className={styles.originalInputRow}>
            <IonInput
              className={styles.originalInput}
              value={originalSubjectName}
              placeholder="教科名を入力"
              onIonChange={e => setOriginalSubjectName(e.detail.value ?? "")}
            />
            <IonButton
              className={styles.addButton}
              size="small"
              disabled={!originalSubjectName.trim() || props.calling}
              onClick={submitOriginalSubject}
            >
              追加
            </IonButton>
          </div>
        </div>
      </div>
      <ModalConfirm
        loading={props.calling}
        isOpen={deleteTargetId !== null}
        whiteMode
        message={
          <>
            削除しますか？
            <br />
            この操作は取り消せません。
          </>
        }
        buttons={[
          {
            title: "キャンセル",
            type: "None",
            action: () => setDeleteTargetId(null),
          },
          {
            title: "削除する",
            type: "Danger",
            action: () => {
              if (deleteTargetId !== null) {
                props.onDeleteOriginalSubject(deleteTargetId);
              }
              setDeleteTargetId(null);
            },
          },
        ]}
        onClose={() => setDeleteTargetId(null)}
      />
    </>
  );
};

export default SubjectSelector;
